import { LocalStorageService } from "src/app/utils/localstorage.service";
import { Job, TaskGroup, Task } from "./job.model";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../../../environments/environment";
import { Observable, Subject } from "rxjs";
import { DashboardService } from "src/app/dashboard/dashboard.service";

@Injectable({ providedIn: "root" })
export class JobsService {
  newRecurringJob = new Subject();
  jobChanged = new Subject();
  jobContentChanged = new Subject();
  newTemplate = new Subject();
  setSelectedJob = new Subject();
  setSelectedJobOnDashboard = new Subject();
  jobContentClosed = new Subject();
  jobsSelectionArray = new Subject();
  successMessageEmitter = new Subject();

  imageUploadUrl = `${environment.apiUrl}/file-manager/`;

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private dashboardService: DashboardService,
  ) {}

  getJob(jobUuid: String): Promise<any> {
    return this.http.get(`${environment.apiUrl}/jobs/${jobUuid}`).toPromise();
  }

  createJob(job: Job): Observable<any> {
    if (
      typeof job["client_company"] == "object" &&
      job["client_company"] != null
    ) {
      job.client_company = job.client_company["id"];
    }

    return this.http.post(`${environment.apiUrl}/jobs`, job);
  }

  createAIJob(companyId, description): Observable<any> {
    return this.http.post(`${environment.apiUrl}/jobs/ai/ai-create`, {
      client_company: companyId,
      description,
    });
  }

  generateEmbeddings(companyId): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/jobs/ai/${companyId}/generate-embedding`,
    );
  }

  askJobQuestion(companyId, question): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/jobs/ai/${companyId}/ask-job-question`,
      { question },
    );
  }

  async saveJob(job: Job): Promise<any> {
    return this.http
      .put(`${environment.apiUrl}/jobs/${job.uuid}`, job)
      .toPromise();
  }

  uploadFiles(job_uuid: string, formData: FormData): Promise<any> {
    const url = `${environment.apiUrl}/jobs/${job_uuid}/file/upload/`;
    return this.http.post(url, formData).toPromise();
  }

  deleteFile(file_id: number) {
    const url = `${environment.apiUrl}/jobs/${file_id}/file/delete/`;
    return this.http.delete(url).toPromise();
  }

  createJobs(clientCompanies: any[], job: Job): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/jobs/batch-create`, {
        client_companies: clientCompanies,
        job_uuid: job.uuid,
        start_date: job.target_start_date,
        due_date: job.due_date,
      })
      .toPromise();
  }

  getJobs(
    filter = null,
    type,
    job_tags = [],
    workspace_tags = [],
    andOrWorkspaces,
    andOrJobs,
    clientFilter = null,
    completedFilter = null,
    dueDateFilter = null,
  ): Observable<any> {
    let params = "";

    // Filter can be 'me', 'everyone' or an user id, therefore we gotta check
    // if it is a string or an object. Couldn't find a better way to do this.
    if (filter) {
      if (typeof filter == "string") {
        params = `?filter=${filter}`;
      } else {
        params = `?user=${filter.id}`;
      }
    }

    if (type) {
      params = params + `&type=${type}`;
    }

    if (andOrWorkspaces) {
      params = params + `&and_or_workspaces=${andOrWorkspaces}`;
    }

    if (andOrJobs) {
      params = params + `&and_or_jobs=${andOrJobs}`;
    }

    if (clientFilter) {
      params = params + `&client_uuid=${clientFilter}`;
    }

    if (completedFilter) {
      params = params + `&completed_filter=${completedFilter}`;
    }

    if (dueDateFilter) {
      params = params + `&due_date_filter=${dueDateFilter}`;
    }

    let job_tag_uuids = job_tags.map((tag) => tag.uuid).join(",");
    let workspace_tag_uuids = workspace_tags.map((tag) => tag.uuid).join(",");

    params =
      params +
      `&job_tags=${job_tag_uuids}&workspace_tags=${workspace_tag_uuids}`;

    return this.http.get(`${environment.apiUrl}/jobs${params}`);
  }

  getJobList(
    filter = null,
    type = null,
    job_tags = [],
    workspace_tags = [],
    dateFilter = null,
    start = 0,
    rows = 25,
    sortField = null,
    sortOrder = null,
    andOrWorkspaces = null,
    andOrJobs = null,
    clientFilter = null,
    loadAll = false,
    completedFilter = null,
    dueDateFilter = null,
  ): Observable<any> {
    let params = "";

    // Filter can be 'me', 'everyone' or an user id, therefore we gotta check
    // if it is a string or an object. Couldn't find a better way to do this.
    if (filter) {
      if (typeof filter == "string") {
        params = `?filter=${filter}`;
      } else {
        params = `?user=${filter.id}`;
      }
    }

    if (type) {
      params = params + `&type=${type}`;
    }

    if (!loadAll) {
      params = params + `&start=${start}&rows=${rows}`;
    }

    if (dateFilter != null) {
      params = params + `&date_filter=${dateFilter}`;
    }

    if (sortField != null) {
      params = params + `&sort_field=${sortField}`;
    }

    if (sortOrder != null) {
      params = params + `&sort_order=${sortOrder}`;
    }

    if (andOrWorkspaces) {
      params = params + `&and_or_workspaces=${andOrWorkspaces}`;
    }

    if (andOrJobs) {
      params = params + `&and_or_jobs=${andOrJobs}`;
    }

    if (clientFilter) {
      params = params + `&client_uuid=${clientFilter}`;
    }

    if (completedFilter) {
      params = params + `&completed_filter=${completedFilter}`;
    }

    if (dueDateFilter) {
      if (Array.isArray(dueDateFilter)) {
        params += `&due_date_filter=custom&start_date=${dueDateFilter[0]}&end_date=${dueDateFilter[1]}`;
      } else {
        params = params + `&due_date_filter=${dueDateFilter}`;
      }
    }

    let job_tag_uuids = job_tags.map((tag) => tag.uuid).join(",");
    let workspace_tag_uuids = workspace_tags.map((tag) => tag.uuid).join(",");

    params =
      params +
      `&job_tags=${job_tag_uuids}&workspace_tags=${workspace_tag_uuids}`;

    return this.http.get(`${environment.apiUrl}/jobs/list${params}`);
  }

  getJobsByStatus(
    company_id,
    status,
    start: number = 0,
    sort_by: string,
    qtyForSorting: number = 0,
  ): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/jobs/${company_id}/${status}?start=${start}&sort_by=${sort_by}&qty_for_sorting=${qtyForSorting}`,
    );
  }

  getJobTemplates(template_type: string): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/jobs/templates/${template_type}`,
    );
  }

  createTaskGroup(job: Job, task_group: TaskGroup): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/jobs/${job.uuid}/taskgroups`,
      task_group,
    );
  }

  saveTaskGroup(task_group: TaskGroup): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}/jobs/taskgroup/${task_group.uuid}`,
      task_group,
    );
  }

  setCurrentTaskGroup(task_group: TaskGroup): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/jobs/taskgroup/${task_group.uuid}/set`,
      {},
    );
  }

  createTask(task_group: TaskGroup, task: Task): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/jobs/taskgroup/${task_group.uuid}/tasks`,
      task,
    );
  }

  saveTask(task: Task): Observable<any> {
    return this.http.put(`${environment.apiUrl}/jobs/task/${task.uuid}`, task);
  }

  deleteTask(task: Task): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/jobs/task/${task.uuid}`);
  }

  deleteTaskGroup(task_group: TaskGroup): Observable<any> {
    return this.http.delete(
      `${environment.apiUrl}/jobs/taskgroup/${task_group.uuid}`,
    );
  }

  jobCompletedConfirmation(jobUuid: string, modalService: any, modal: any, toastr: any) {
    let initialState = {
      title: null,
      message: `This client task was the last remaining open task in the job that is linked to. Do you want to mark the job status as Completed?`,
      cancelLabel: "No",
      confirmLabel: "Yes",
      confirmButtonClass: "btn-primary",
    };

    let confirmationModal = modalService.show(modal, {
      initialState,
    });

    confirmationModal.content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.successMessageEmitter.subscribe((msg: string) => toastr.success(msg));
        this.markJobCompleted(jobUuid);
      }
    });
  }

  markJobCompleted(jobUuid) {
    this.getJob(jobUuid).then(
      (job: Job) => {
        job.status = 3;
        this.saveJob(job).then(() => this.successMessageEmitter.next("Job updated!"));
      }
    )
  }

  duplicateJob(
    job_uuid,
    client_company_id,
    override_params = {},
    from_template = false,
  ): Observable<any> {
    return this.http.post(`${environment.apiUrl}/jobs/${job_uuid}/duplicate`, {
      client_company_id,
      override_params,
      from_template,
    });
  }

  deleteJob(job: Job, createInstance: boolean): Observable<any> {
    return this.http.delete(
      `${environment.apiUrl}/jobs/${job.uuid}?instance=${createInstance}`,
    );
  }

  publishTrackedItem(company_id: number, tracked_item_uuid: string) {
    return this.http.post(
      `${environment.apiUrl}/conversations/${company_id}/publish/draft/${tracked_item_uuid}`,
      {},
    );
  }

  updateTaskGroupOrder(job_uuid, task_groups): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}/jobs/${job_uuid}/taskgroups/order`,
      task_groups,
    );
  }

  updateTaskOrder(job_uuid, task_group_id, tasks): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}/jobs/${job_uuid}/taskgroups/${task_group_id}/tasks/order`,
      tasks,
    );
  }

  setHeaders(ev) {
    ev.editor.on("fileUploadRequest", (evt) => {
      this.dashboardService.show_loading.next(true);
      console.log("setHeader", evt);
      var xhr = evt.data.fileLoader.xhr;
      var token = this.localStorage.get("access_token");
      xhr.setRequestHeader("Authorization", "JWT " + token);
    });

    ev.editor.on("fileUploadResponse", (evt) => {
      this.dashboardService.show_loading.next(false);
    });
  }

  createJobFromMessage(
    job: Job,
    task_name: string,
    message: any,
  ): Observable<any> {
    const payload = { task_name, message, ...job };
    return this.http.post(`${environment.apiUrl}/jobs/from-message`, payload);
  }

  getJobComments(jobUuid: string, idx = 0) {
    let url = `${environment.apiUrl}/jobs/${jobUuid}/comments`;
    url += idx ? `?startAt=${idx}` : "";
    return this.http.get(url);
  }

  comment(jobUuid: string, text: string, mentions: number[]) {
    return this.http.post(`${environment.apiUrl}/jobs/${jobUuid}/comments`, {
      text,
      mentions,
    });
  }

  editComment(commentUuid: string, text: string) {
    return this.http.patch(
      `${environment.apiUrl}/jobs/${commentUuid}/comments`,
      {
        text,
      },
    );
  }

  restoreComment(commentUuid: string) {
    return this.http.put(
      `${environment.apiUrl}/jobs/${commentUuid}/comments`,
      {},
    );
  }

  deleteComment(commentUuid: string) {
    return this.http.delete(
      `${environment.apiUrl}/jobs/${commentUuid}/comments`,
    );
  }

  getAccountantsFromClientCompany(clientCompanyId: number): Promise<any> {
    const url = `${environment.apiUrl}/jobs/${clientCompanyId}/get-accountants`;
    return this.http.get(url).toPromise();
  }

  /** DEPRECATED */
  changeOwnership(newOwner: any, jobs: string[], setOnRepeatJobs) {
    const url = `${environment.apiUrl}/jobs/batch/change-ownership`;
    return this.http
      .post(url, { user: newOwner.user, jobs, setOnRepeatJobs })
      .toPromise();
  }

  changeJobOwnership(newOwner: any, jobs: string[], setOnRepeatJobs) {
    const url = `${environment.apiUrl}/jobs/batch/change-ownership`;
    return this.http
      .post(url, { user: newOwner.user, jobs, setOnRepeatJobs });
  }

  reassignJobsAndTasks(newUserId: number, jobs: string[], oldUserId: number) {
    const url = `${environment.apiUrl}/user/${oldUserId}/check-assignments`;
    return this.http
      .post(url, { new_user_id: newUserId, jobs, old_user_id: oldUserId })
      .toPromise();
  }

  manageTags(action: "add" | "remove", jobs: string[], tags: string[]) {
    const url = `${environment.apiUrl}/jobs/batch/manage-tags`;
    return this.http.post(url, { action, jobs, tags }).toPromise();
  }

  updateStatuses(data) {
    const url = `${environment.apiUrl}/jobs/batch/update-status`;
    return this.http.put(url, { data }).toPromise();
  }

  /* ============== SYNCFUSION SETTINGS CODE =============== */
  public onUploadSuccess(args) {
    let response = JSON.parse(args.e.currentTarget.response);
    if (response.url != null) {
      args.file.name = response.etag;
    }
  }

  public addHeaders(args: any) {
    var token = this.localStorage.get("access_token");
    args.currentRequest.setRequestHeader("Authorization", `JWT ${token}`);
  }

  public insertImageSettings: Object = {
    path: `${environment.apiUrl}/file-manager/public-media/`,
    saveUrl: this.imageUploadUrl,
  };

  public toolbarSettings: Object = {
    type: "MultiRow",
    items: [
      "Bold",
      "Italic",
      "Underline",
      "StrikeThrough",
      "|",
      "CreateLink",
      "Image",
      "|",
      "FontName",
      "FontSize",
      "FontColor",
      "BackgroundColor",
      "LowerCase",
      "UpperCase",
      "|",
      "Formats",
      "Alignments",
      "OrderedList",
      "UnorderedList",
      "Outdent",
      "Indent",
      "|",
      "ClearFormat",
      "|",
      "Undo",
      "Redo",
    ],
  };

  public clientNotesTaskDetailsToolbarSettings: Object = {
    type: "MultiRow",

    items: [
      "Bold",
      "Italic",
      "Underline",
      "StrikeThrough",
      "FontColor",
      "BackgroundColor",
      "FontSize",
      "|",
      "CreateLink",
      "Image",
      // "|",
      // "FontName",
      "|",
      // "Formats",
      "Alignments",
      "OrderedList",
      "UnorderedList",
      "Outdent",
      "Indent",
      "|",
      "Undo",
      "Redo",
    ],
  };

  public clientTaskToolbarSettings: Object = {
    type: "MultiRow",
    items: [
      "Bold",
      "Italic",
      "Underline",
      "StrikeThrough",
      // "FontColor",
      // "BackgroundColor",
      "|",
      "CreateLink",
      "Image",
      "|",
      "OrderedList",
      "UnorderedList",
      // "FontName",
      // "FontSize",
    ],
  };

  public emailComposeToolbarSettings: Object = {
    type: "MultiRow",
    items: [
      "Bold",
      "Italic",
      "Underline",
      "StrikeThrough",
      // "FontColor",
      // "BackgroundColor",
      "|",
      "CreateLink",
      "|",
      "OrderedList",
      "UnorderedList",
      // "FontName",
      // "FontSize",
    ],
  };

  public saveInterval: number = 1000;
  /* ========== END SYNCFUSION SETTINGS CODE ================ */
}
